/**
 * Global axios interceptor for admin auth.
 *
 * In production, frontend and backend can end up on slightly different origins
 * (custom domain vs. emergent host) which makes the httpOnly cookie unreliable
 * on Safari/Chrome with SameSite=Lax. To avoid the resulting login → 401 → loop,
 * we ALSO send the JWT as an `Authorization: Bearer` header on every /api/admin/
 * request. The backend's dual-mode `get_current_admin` accepts either path.
 *
 * Storage: `localStorage` so the session survives tab close/reload. This trades
 * a small XSS exposure for reliable login UX — explicitly chosen.
 */
import axios from 'axios';

const TOKEN_KEY = 'adminToken';

export function setAdminToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

// Staff signed in on this browser — their visits are left out of analytics.
const DONT_COUNT_KEY = 'lc_dont_count';
export const isDontCountDevice = () => localStorage.getItem(DONT_COUNT_KEY) === '1';
export function setDontCountDevice(on) {
  // Cookie lets the server skip file downloads from this device too.
  if (on) {
    localStorage.setItem(DONT_COUNT_KEY, '1');
    document.cookie = `${DONT_COUNT_KEY}=1; path=/; max-age=${60 * 60 * 24 * 365 * 5}; SameSite=Lax`;
  } else {
    localStorage.removeItem(DONT_COUNT_KEY);
    document.cookie = `${DONT_COUNT_KEY}=; path=/; max-age=0; SameSite=Lax`;
  }
}
export const isStaffBrowser = () => !!getAdminToken() || isDontCountDevice();

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/** Install the request interceptor exactly once. */
let installed = false;
export function installAdminAuthInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    // Only attach on admin API calls — keep public endpoints anonymous.
    const url = config.url || '';
    if (url.includes('/api/admin/')) {
      const token = getAdminToken();
      if (token) {
        config.headers = config.headers || {};
        // Don't overwrite an explicit header the caller set.
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      // Belt-and-suspenders: still send cookies when same-origin allows it.
      config.withCredentials = true;
    }
    return config;
  });
}
